import React, { useEffect, useState } from "react";
import {
  BottomSheet,
  Button,
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  SkeletonCard,
  useEndReached,
} from "../../../components/ui";
import { formatPrice } from "../planFormat";
import {
  PLAN_STATUS_PILL,
  PLAN_STATUS_STYLES,
} from "../planStatusStyles";
import { BrandPlan } from "../types/brand.types";
import SupportChannelList from "./SupportChannelList";

/** The height the cards stand in at while the list is on its way — about three
 *  cards, which is what a phone shows before the fold. */
const CARDS_SKELETON = "h-[300px]";

/** One label-and-value line in the sheet. The label is fixed-width so the
 *  values line up down the sheet rather than each starting wherever its own
 *  label happened to end. */
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-baseline gap-3 py-2.5">
    <dt className="w-[104px] flex-shrink-0 text-[13px] text-ink-muted">
      {label}
    </dt>
    <dd className="min-w-0 flex-1 text-[14px] text-ink">{children}</dd>
  </div>
);

/**
 * One of the three counts, or nothing at all where the plan does not put a
 * number on it.
 *
 * Nothing rather than a dash: these are optional facts about what is being
 * sold, and a plan that says none of them would otherwise open to three
 * rows of dashes before it got to anything worth reading. Support is not
 * treated this way — it is a single line, and its emptiness is an answer.
 */
const CountField: React.FC<{
  label: string;
  count: number | null;
  /** Written after the number — "3 per meal" reads where "3" alone does not. */
  suffix?: string;
}> = ({ label, count, suffix }) =>
  count === null ? null : (
    <Field label={label}>
      {count}
      {suffix ? ` ${suffix}` : ""}
    </Field>
  );

interface PlanCardsProps {
  plans: BrandPlan[];
  /**
   * The plan whose record is open in the sheet, or `null` for none.
   *
   * The tab's state rather than this list's: both of the sheet's actions put
   * something else on screen and take the sheet down with them, and it is the
   * tab that knows when that something is finished with — so it is the tab
   * that can put the sheet back where it was.
   */
  selected: BrandPlan | null;
  /** Opens the sheet on a plan, or shuts it with `null`. */
  onSelect: (plan: BrandPlan | null) => void;
  /** Stands in for the cards when the list comes back with none. */
  empty: EmptyStateProps;
  /** Where the list is — the same contract `PlansTable` works to. */
  phase?: LoadPhase;
  onEdit: (plan: BrandPlan) => void;
  /**
   * Confirmed inside the sheet, so this is the call rather than a request for
   * a dialog. `active` is where the plan is going.
   */
  onSetActive: (plan: BrandPlan, active: boolean) => void;
  /** True while a listing change is with the server; the buttons read it. */
  saving?: boolean;
  /** Whether the caller is holding plans past the ones it has handed over. */
  hasMore?: boolean;
  /** Asks for the next batch — see `useEndReached`. */
  onEndReached?: () => void;
}

/**
 * The plans list as it reads on a phone: one card per plan instead of a
 * table, and no card around the lot of them.
 *
 * The table needs 760px to lay its six columns out, which is more than a phone
 * has — so on one it either clips the price and the status off the right edge
 * or asks the reader to scroll sideways through a card that is itself inside a
 * page. The row becomes a card of its own on the page's own background
 * instead, carrying what the eye runs down the list for: what the plan is,
 * how long it runs, what it costs and whether it is listed.
 *
 * Pressing one opens the record in a bottom sheet, which is also where Edit
 * and Deactivate live. That is the sheet's real work: a plan has five
 * fields and the card already shows four of them, but the row menu the table
 * uses is a popup anchored to the end of a row, and a phone's row has no end
 * to anchor to. A sheet arrives under the thumb, names the plan it belongs
 * to, and has room for both actions as buttons rather than as a fingertip
 * target.
 *
 * The listing change is confirmed in the sheet itself, the way the service
 * details panel asks its own question: a dialog over a sheet is two layers
 * over a list, both of them trapping focus and both of them locking the page
 * behind — and the question is about the plan already on screen, so the sheet
 * asks it where the plan is and the answer is one press from where it was
 * raised. The table keeps its own dialog, having no sheet to ask inside.
 *
 * Laid out the same way `PaymentCards` is, since the two read as the same
 * screen: a list of records with a status, on the same background, at the same
 * corner.
 *
 * Which of the two renders is `BrandPlansTab`'s decision — see `useIsMobile`.
 */
const PlanCards: React.FC<PlanCardsProps> = ({
  plans,
  empty,
  phase = "ready",
  selected,
  onSelect,
  onEdit,
  onSetActive,
  saving = false,
  hasMore = false,
  onEndReached,
}) => {
  const { ref, onScroll } = useEndReached<HTMLDivElement>(hasMore, onEndReached);

  // While set, the sheet is asking rather than showing. Reset whenever the
  // plan it is open on changes — which is both a sheet closed mid-question
  // and a listing change that came back saved, since the tab hands the saved
  // plan straight back in.
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (selected) setConfirming(false);
  }, [selected]);

  // Which way the question is pointing. Read off the plan rather than stored
  // beside `confirming`, so one flag cannot disagree with the card it came
  // from.
  const deactivating = selected?.status === "Active";

  const close = () => {
    if (saving) return;
    onSelect(null);
  };

  /** Hands the plan to the form and gets out of the way: the form is another
   *  panel, and a sheet left standing under it is two layers over a list
   *  nobody can see. The listing change needs none of this — it is answered
   *  here. */
  const edit = () => {
    if (!selected) return;
    const plan = selected;
    onSelect(null);
    onEdit(plan);
  };

  if (phase !== "ready" && plans.length === 0) {
    return (
      <div className="flex-1 min-h-0">
        <SkeletonCard phase={phase} className={CARDS_SKELETON} />
      </div>
    );
  }

  if (plans.length === 0) {
    // A column rather than a row, so the state gets the full width to set its
    // lines in — centred as a row it would be sized by its longest word and
    // could push past a phone's edge. It brings its own padding.
    return (
      <div className="flex-1 min-h-0 flex flex-col justify-center">
        <EmptyState {...empty} />
      </div>
    );
  }

  return (
    <>
      <div
        ref={ref}
        onScroll={onScroll}
        className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-overlay"
      >
        <ul className="flex flex-col gap-3 pb-4">
          {plans.map((plan) => (
            <li key={plan.id}>
              <button
                type="button"
                onClick={() => onSelect(plan)}
                aria-haspopup="dialog"
                // The whole plan names it, as the table's row menu does:
                // the same name and duration can appear twice at different
                // prices, so the price is what makes the label unambiguous.
                aria-label={`${plan.name}, ${plan.duration}, ${formatPrice(
                  plan.price
                )}, ${plan.status}`}
                // The same corner the rest of the panel's cards wear — two
                // radii a thumb's width apart read as two different surfaces.
                className="w-full card-surface border border-line bg-white p-4 text-left"
              >
                <div className="flex items-start justify-between gap-3">
                  {/* `min-w-0` is what lets a long plan name shorten
                      instead of pushing the pill off the card's edge. */}
                  <div className="min-w-0">
                    <h3 className="truncate text-[15px] font-medium text-ink">
                      {plan.name}
                    </h3>
                    <p className="mt-0.5 truncate text-[13px] text-ink-muted">
                      {plan.duration}
                    </p>
                  </div>
                  <span
                    className={`${PLAN_STATUS_PILL} flex-shrink-0 ${
                      PLAN_STATUS_STYLES[plan.status]
                    }`}
                  >
                    {plan.status}
                  </span>
                </div>

                <div className="mt-3 flex items-baseline justify-between gap-3 border-t border-line pt-3">
                  <span className="text-[16px] font-semibold text-ink">
                    {formatPrice(plan.price)}
                  </span>
                  <span className="text-[12px] text-ink-subtle">
                    {plan.createdOn}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* The record, and what can be done to it. Titled by the plan, since
          that is what was pressed to get here. */}
      <BottomSheet
        open={!!selected}
        title={selected?.name ?? ""}
        onClose={close}
        // Equal halves, because neither is the obvious one: changing a price
        // and taking a plan off the public page are different jobs rather
        // than a choice and its cancel. Delete is not offered — there is no
        // endpoint behind it, and deactivating is what takes a plan down.
        //
        // The listing change sits on the left and Edit on the right, in the
        // service sheet's order: the two panels open the same way from the
        // same kind of card, and a thumb that has learned where Edit is on
        // one should not have to look again on the other. It is the quiet
        // button of the pair here as it is there — red is kept for the
        // question below, where the plan actually comes down.
        //
        // While asking, the same row is the answer and the way out of it.
        footer={
          selected &&
          (confirming ? (
            <>
              <Button
                variant="secondary"
                size="md"
                onClick={() => setConfirming(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant={deactivating ? "danger" : "primary"}
                size="md"
                loading={saving}
                onClick={() => onSetActive(selected, !deactivating)}
              >
                {deactivating ? "Deactivate" : "Activate"}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="secondary"
                size="md"
                onClick={() => setConfirming(true)}
              >
                {deactivating ? "Deactivate" : "Activate"}
              </Button>
              <Button size="md" onClick={edit}>
                Edit
              </Button>
            </>
          ))
        }
      >
        {selected && confirming && (
          <p className="px-5 pb-2 pt-1 text-[14px] leading-[1.6] text-ink-muted">
            {/* The sheet's heading is the plan's name and nothing else, and
                the same name can be sold twice over at two lengths and two
                prices — so the question names which of them is about to
                move, rather than trusting a heading to have said it. */}
            <span className="text-ink">
              {selected.duration}, {formatPrice(selected.price)}.
            </span>{" "}
            {deactivating
              ? "This plan will be hidden until it is activated again."
              : "This plan will be available after activation."}
          </p>
        )}

        {selected && !confirming && (
          <dl className="divide-y divide-[#F1F4F6] px-5 pb-2">
            <Field label="Price">{formatPrice(selected.price)}</Field>
            <Field label="Duration">{selected.duration}</Field>

            {/* What the plan comes with. The table writes these as one
                bar-separated line under the name, because a cell is all the
                room a column has; a sheet has a line each, so each gets its
                own label rather than the reader having to work out which
                number was which. */}
            <CountField label="Consultations" count={selected.consultations} />
            <CountField label="Meal plans" count={selected.mealPlans} />
            <CountField
              label="Meal options"
              count={selected.mealOptionsPerMeal}
              suffix="per meal"
            />
            <Field label="Support">
              {/* Named as well as drawn: the sheet has the width for the
                  words, and this is where someone comes to find out what a
                  plan actually includes. */}
              <SupportChannelList channels={selected.supportChannels} withLabels />
            </Field>

            <Field label="Status">
              <span
                className={`${PLAN_STATUS_PILL} ${
                  PLAN_STATUS_STYLES[selected.status]
                }`}
              >
                {selected.status}
              </span>
            </Field>
            <Field label="Created on">{selected.createdOn}</Field>
          </dl>
        )}
      </BottomSheet>
    </>
  );
};

export default PlanCards;
